import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronUp, ChevronDown, Mouse, ZoomIn, Zap } from 'lucide-react';

/* ============================================================
   WEAPONS & COMBAT — Bright AAA arsenal interface
   Large glass preview + stats + right-side curved gun carousel
   ============================================================ */

const CRIMSON = '#FF8618';
const NAVY = 'var(--text-primary)';
const STEEL = 'var(--text-muted)';

const WEAPONS = [
  { id: 1, num: '01', slug: 'ar25', name: 'AR-25 NOVA', category: 'Assault Rifle', klass: 'KINETIC',
    description: 'Reliable kinetic rifle built for sustained, controlled fire.',
    damage: 28, range: 'Mid-Range', fireRate: 'High', magazine: 32 },
  { id: 2, num: '02', slug: 'vx19', name: 'VX19 HUNTER', category: 'Assault Rifle', klass: 'TACTICAL',
    description: 'Precision tactical rifle tuned for disciplined burst fire.',
    damage: 30, range: 'Mid-Range', fireRate: 'Medium', magazine: 28 },
  { id: 3, num: '03', slug: 'ar32', name: 'AR-32 PHOENIX', category: 'Assault Rifle', klass: 'PLASMA',
    description: 'Versatile plasma rifle with balanced stats.',
    damage: 32, range: 'Mid-Range', fireRate: 'High', magazine: 30,
    glb: '/models/ar32_phoenix_compressed.glb' },
  { id: 4, num: '04', slug: 'm47', name: 'M-47 REAPER', category: 'Heavy Rifle', klass: 'BALLISTIC',
    description: 'Heavy ballistic rifle that shreds armor at close range.',
    damage: 46, range: 'Short-Range', fireRate: 'Medium', magazine: 45 },
  { id: 5, num: '05', slug: 'plasma9', name: 'PLASMA-9', category: 'Plasma Rifle', klass: 'PLASMA',
    description: 'Overcharged plasma core delivering devastating energy bursts.',
    damage: 38, range: 'Mid-Range', fireRate: 'High', magazine: 24 },
];

const THROWABLES = [
  { id: 1, name: 'FRAG', type: 'Explosive' },
  { id: 2, name: 'EMP', type: 'Tech Disruptor' },
  { id: 3, name: 'FLASH', type: 'Blinding' },
  { id: 4, name: 'SMOKE', type: 'Cover' },
  { id: 5, name: 'TOXIN', type: 'Damage / Time' },
];

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

const GunImg = ({ slug, name, className, style }) => {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Zap className="w-1/3 h-1/3" style={{ color: CRIMSON, opacity: 0.7 }} />
      </div>
    );
  }
  return <img src={`/weapons/${slug}.png`} alt={name} className={className} style={style} draggable={false} onError={() => setErr(true)} />;
};

const Weapons = () => {
  const [activeTab, setActiveTab] = useState('weapons');
  const [activeIdx, setActiveIdx] = useState(2); // AR-32 Phoenix
  const active = WEAPONS[activeIdx];

  // faux 3D inspect (for image guns)
  const [tilt, setTilt] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pvDragging, setPvDragging] = useState(false);

  const previewDrag = useRef({ x: 0, active: false });
  const onPreviewDown = (e) => { if (active.glb) return; previewDrag.current = { x: e.clientX, active: true }; setPvDragging(true); };
  const onPreviewMove = (e) => {
    if (!previewDrag.current.active) return;
    const dx = e.clientX - previewDrag.current.x;
    previewDrag.current.x = e.clientX;
    setTilt((t) => clamp(t + dx * 0.5, -40, 40));
  };
  const onPreviewUp = () => { previewDrag.current.active = false; setPvDragging(false); };
  const previewStageRef = useRef(null);
  useEffect(() => {
    const el = previewStageRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (active.glb) return;
      e.preventDefault();
      setZoom((z) => clamp(z - e.deltaY * 0.0012, 0.7, 1.7));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [active.glb]);

  // carousel scroll / drag
  const carRef = useRef(null);
  const wheelLock = useRef(false);
  const carDrag = useRef({ y: 0, active: false, fired: false });
  const move = useCallback((dir) => { setActiveIdx((i) => clamp(i + dir, 0, WEAPONS.length - 1)); setTilt(0); setZoom(1); }, []);
  useEffect(() => {
    const el = carRef.current;
    if (!el) return;
    const onWheel = (e) => {
      const dir = e.deltaY > 0 ? 1 : -1;
      // let the page scroll past once we hit the ends (don't trap)
      const atEnd = (dir === 1 && activeIdx === WEAPONS.length - 1) || (dir === -1 && activeIdx === 0);
      if (atEnd) return;
      e.preventDefault();
      if (wheelLock.current) return;
      wheelLock.current = true;
      move(dir);
      setTimeout(() => { wheelLock.current = false; }, 100);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [move, activeIdx]);
  const onCarDown = (e) => { carDrag.current = { y: e.clientY, active: true, fired: false }; };
  const onCarMove = (e) => {
    const d = carDrag.current;
    if (!d.active || d.fired) return;
    const dy = e.clientY - d.y;
    if (Math.abs(dy) > 40) { move(dy > 0 ? 1 : -1); d.fired = true; }
  };
  const onCarUp = () => { carDrag.current.active = false; };

  return (
    <section
      id="weapons"
      data-testid="weapons-section"
      className="relative overflow-hidden py-12 md:py-14 px-5 sm:px-8"
    >
      {/* soft readability veil */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 90% 70% at 50% 40%, rgba(255,134,24,0.07) 0%, transparent 70%)', backdropFilter: 'blur(1.5px)', WebkitBackdropFilter: 'blur(1.5px)' }} />

      <div className="relative z-10 max-w-[1480px] mx-auto">
        {/* ===== HEADER ===== */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <Zap className="w-8 h-8 flex-shrink-0" style={{ color: CRIMSON, fill: CRIMSON, filter: 'drop-shadow(0 2px 8px rgba(255,134,24,0.5))', transform: 'rotate(6deg)' }} />
            <h2
              className="font-black uppercase leading-[0.92]"
              style={{ fontFamily: '"Russo One","Rajdhani",sans-serif', fontSize: 'clamp(2.4rem, 6vw, 4.6rem)', letterSpacing: '0.01em' }}
              data-testid="weapons-title"
            >
              <span style={{ color: NAVY }}>WEAPONS </span>
              <span style={{ color: CRIMSON }}>&amp; COMBAT</span>
            </h2>
          </div>
          <p className="mt-3 uppercase" style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 600, color: STEEL, fontSize: '12px', letterSpacing: '0.16em' }}>
            Live 3D preview — Pick a weapon below to inspect from every angle.
          </p>
        </div>

        {/* ===== TABS ===== */}
        <div className="flex gap-2 mb-7">
          {[{ id: 'weapons', label: 'WEAPONS' }, { id: 'throwables', label: 'THROWABLES' }].map((t) => {
            const on = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`${t.id}-tab`}
                className="px-8 py-2.5 text-xs uppercase transition-all"
                style={{
                  fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, letterSpacing: '0.22em',
                  color: on ? '#FFFFFF' : 'var(--text-muted)',
                  background: on ? 'linear-gradient(180deg,#FF8618,#F07310)' : 'rgba(20,27,34,0.7)',
                  border: on ? '1px solid #FF8618' : '1px solid rgba(255,255,255,0.14)',
                  clipPath: 'polygon(10% 0, 100% 0, 90% 100%, 0 100%)',
                  boxShadow: on ? '0 6px 18px rgba(255,134,24,0.35)' : 'none',
                  backdropFilter: 'blur(6px)',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'weapons' ? (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_clamp(300px,24vw,380px)] gap-5 lg:gap-4 items-stretch">
            {/* ===== BIG GLASS PANEL: PREVIEW + DETAILS ===== */}
            <div
              className="relative grid grid-cols-1 md:grid-cols-[1.25fr_1fr] rounded-[22px] overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(16,22,28,0.72) 0%, rgba(22,29,36,0.6) 100%)',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 24px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
                backdropFilter: 'blur(16px) saturate(120%)',
                WebkitBackdropFilter: 'blur(16px) saturate(120%)',
              }}
            >
              {/* PREVIEW COLUMN */}
              <div className="relative p-6 md:p-7 flex flex-col" style={{ borderRight: '1px solid rgba(255,255,255,0.11)' }}>
                <span className="text-[11px] uppercase" style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, letterSpacing: '0.3em' }}>
                  <span style={{ color: CRIMSON }}>01</span>
                  <span style={{ color: STEEL }}> / PREVIEW</span>
                </span>

                <div
                  ref={previewStageRef}
                  className="relative flex-1 flex items-center justify-center mt-2 select-none"
                  style={{ minHeight: '360px', cursor: active.glb ? 'default' : 'grab' }}
                  onPointerDown={onPreviewDown}
                  onPointerMove={onPreviewMove}
                  onPointerUp={onPreviewUp}
                  onPointerLeave={onPreviewUp}
                  data-testid="weapon-preview-stage"
                >
                  {/* glowing platform */}
                  <div className="absolute left-1/2 -translate-x-1/2 pointer-events-none" style={{ bottom: '6%', width: '78%', height: '120px' }}>
                    {[0.5, 0.72, 1].map((s, i) => (
                      <div
                        key={i}
                        className="weapon-platform-ring absolute left-1/2 -translate-x-1/2 rounded-[50%]"
                        style={{
                          bottom: `${i * 12}px`, width: `${s * 100}%`, height: `${s * 70}px`,
                          border: `1px solid rgba(255,134,24,${0.6 - i * 0.12})`,
                          boxShadow: `0 0 22px rgba(255,134,24,${0.45 - i * 0.1})`,
                          animationDelay: `${i * 0.4}s`,
                        }}
                      />
                    ))}
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-0 rounded-[50%]" style={{ width: '60%', height: '36px', background: 'radial-gradient(ellipse, rgba(255,134,24,0.5), transparent 70%)', filter: 'blur(6px)' }} />
                  </div>

                  {/* gun */}
                  {active.glb ? (
                    <iframe
                      key={active.slug}
                      src={`/model-viewer.html?src=${encodeURIComponent(active.glb)}`}
                      title={active.name}
                      style={{ width: '100%', height: '100%', minHeight: '360px', border: 'none', background: 'transparent' }}
                      data-testid="weapon-preview-3d"
                    />
                  ) : (
                    <GunImg
                      slug={active.slug}
                      name={active.name}
                      className="weapon-gun-float relative z-10 max-w-[90%] max-h-[300px] object-contain"
                      style={{ transform: `perspective(900px) rotateY(${tilt}deg) scale(${zoom})`, transition: pvDragging ? 'none' : 'transform 0.3s ease', filter: 'drop-shadow(0 16px 26px rgba(20,40,60,0.4))' }}
                    />
                  )}
                </div>

                {/* control hints */}
                <div className="flex items-center justify-center gap-5 mt-2 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.11)' }}>
                  <span className="flex items-center gap-2 uppercase" style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 600, fontSize: '11px', letterSpacing: '0.16em', color: 'var(--text-muted)' }}>
                    <Mouse className="w-4 h-4" style={{ color: STEEL }} /> Drag to rotate
                  </span>
                  <span className="w-px h-4" style={{ background: 'rgba(255,255,255,0.18)' }} />
                  <span className="flex items-center gap-2 uppercase" style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 600, fontSize: '11px', letterSpacing: '0.16em', color: 'var(--text-muted)' }}>
                    <ZoomIn className="w-4 h-4" style={{ color: STEEL }} /> Scroll to zoom
                  </span>
                </div>
              </div>

              {/* DETAILS COLUMN */}
              <div className="relative p-6 md:p-8 flex flex-col">
                <span className="text-xs uppercase mb-3" style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, letterSpacing: '0.28em', color: CRIMSON }} data-testid="weapon-active-category">
                  {active.category}
                </span>
                <h3
                  className="font-black uppercase leading-[1] mb-4"
                  style={{ fontFamily: '"Russo One","Rajdhani",sans-serif', fontSize: 'clamp(1.9rem,3vw,2.7rem)', color: NAVY, letterSpacing: '0.01em' }}
                  data-testid="weapon-active-name"
                >
                  {active.name}
                </h3>
                <span
                  className="self-start text-xs uppercase px-4 py-1.5 mb-5"
                  style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, letterSpacing: '0.22em', background: '#FF8618', color: '#fff', boxShadow: '0 4px 14px rgba(255,134,24,0.35)' }}
                  data-testid="weapon-active-class"
                >
                  {active.klass}
                </span>
                <p className="text-sm leading-relaxed mb-6" style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 500, color: 'var(--text-secondary)' }}>
                  {active.description}
                </p>
                <div className="h-px w-full mb-6" style={{ background: 'rgba(255,255,255,0.12)' }} />
                <div className="grid grid-cols-2 gap-x-6 gap-y-7 mt-auto">
                  <Stat label="Damage" value={String(active.damage)} />
                  <Stat label="Range" value={active.range} />
                  <Stat label="Fire Rate" value={active.fireRate} />
                  <Stat label="Magazine" value={String(active.magazine)} />
                </div>
              </div>
            </div>

            {/* ===== SCROLL HINT COLUMN ===== */}
            <div className="hidden lg:flex flex-col items-center justify-center gap-3 px-1" data-testid="weapon-scroll-hint">
              <ChevronUp className="weapon-scroll-chev w-5 h-5" style={{ color: CRIMSON }} />
              <Mouse className="w-5 h-5" style={{ color: STEEL }} />
              <span
                className="uppercase"
                style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: '10px', letterSpacing: '0.3em', color: STEEL, writingMode: 'vertical-rl', textOrientation: 'mixed' }}
              >
                Drag or scroll to browse
              </span>
              <ChevronDown className="weapon-scroll-chev w-5 h-5" style={{ color: CRIMSON, animationDelay: '0.9s' }} />
            </div>

            {/* ===== RIGHT CURVED CAROUSEL ===== */}
            <div
              className="relative rounded-[22px] p-4 sm:p-5"
              style={{
                background: 'linear-gradient(135deg, rgba(16,22,28,0.72) 0%, rgba(22,29,36,0.58) 100%)',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 24px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
                backdropFilter: 'blur(16px) saturate(120%)',
                WebkitBackdropFilter: 'blur(16px) saturate(120%)',
              }}
            >
              {/* header */}
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-[11px] uppercase" style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, letterSpacing: '0.26em' }}>
                  <span style={{ color: CRIMSON }}>02</span>
                  <span style={{ color: STEEL }}> / SELECT WEAPON</span>
                </span>
                <ChevronUp className="w-4 h-4" style={{ color: CRIMSON }} />
              </div>

              <div className="flex gap-2">
                {/* ruler ticks */}
                <div className="flex flex-col justify-between py-2" aria-hidden style={{ width: '8px' }}>
                  {Array.from({ length: 22 }).map((_, i) => (
                    <span key={i} className="block" style={{ height: '1px', width: i % 5 === 0 ? '8px' : '4px', background: i % 5 === 0 ? 'rgba(255,134,24,0.6)' : 'rgba(21,33,46,0.22)' }} />
                  ))}
                </div>

                {/* cards */}
                <div
                  ref={carRef}
                  className="flex-1 flex flex-col gap-2.5 py-1"
                  style={{ perspective: '1500px', touchAction: 'pan-y' }}
                  onPointerDown={onCarDown}
                  onPointerMove={onCarMove}
                  onPointerUp={onCarUp}
                  onPointerLeave={onCarUp}
                  data-testid="weapon-carousel"
                >
                  {WEAPONS.map((w, i) => {
                    const on = i === activeIdx;
                    const dist = Math.abs(i - activeIdx);
                    return (
                      <button
                        key={w.id}
                        onClick={() => { setActiveIdx(i); setTilt(0); setZoom(1); }}
                        data-testid={`weapon-card-${w.slug}`}
                        className="weapon-card-slide relative flex items-center gap-2 text-left rounded-[14px] overflow-hidden focus:outline-none"
                        style={{
                          padding: on ? '14px 12px' : '9px 11px',
                          transformStyle: 'preserve-3d',
                          transform: on ? 'translateZ(46px) scale(1.02)' : `translateZ(0) rotateY(-6deg) scale(${1 - dist * 0.04})`,
                          background: on ? 'linear-gradient(135deg, rgba(40,16,18,0.92), rgba(20,12,14,0.92))' : 'rgba(18,24,34,0.82)',
                          border: on ? '2px solid #FF8618' : '1px solid rgba(120,138,152,0.28)',
                          boxShadow: on ? '0 0 0 4px rgba(255,134,24,0.12), 0 0 30px rgba(255,134,24,0.55), 0 16px 30px rgba(0,0,0,0.4)' : '0 8px 18px rgba(0,0,0,0.28)',
                          opacity: on ? 1 : clamp(1 - dist * 0.14, 0.55, 1),
                          zIndex: on ? 10 : 5,
                        }}
                      >
                        {on && <span className="absolute -left-[3px] top-1/2 -translate-y-1/2 w-4 h-4 rounded-full" style={{ background: CRIMSON, boxShadow: `0 0 14px ${CRIMSON}` }} />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span style={{ fontFamily: '"Russo One",sans-serif', fontSize: on ? '20px' : '14px', color: on ? CRIMSON : 'var(--text-secondary)', lineHeight: 1 }}>{w.num}</span>
                            {on && <span className="block h-px flex-1" style={{ background: CRIMSON }} />}
                          </div>
                          <p className="truncate mt-1.5" style={{ fontFamily: '"Russo One","Rajdhani",sans-serif', fontWeight: 700, fontSize: on ? '15px' : '12px', color: 'var(--text-primary)', letterSpacing: '0.03em' }}>{w.name}</p>
                          <p className="truncate" style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 500, fontSize: on ? '11px' : '10px', color: 'var(--text-muted)' }}>{w.category}</p>
                          {on && (
                            <span className="inline-block mt-2 px-3 py-1 uppercase" data-testid="weapon-active-badge" style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: '10px', letterSpacing: '0.2em', background: '#FF8618', color: '#fff' }}>
                              Active
                            </span>
                          )}
                        </div>
                        <div className="flex-shrink-0 flex items-center justify-center" style={{ width: on ? '108px' : '74px', height: on ? '74px' : '50px' }}>
                          <GunImg slug={w.slug} name={w.name} className="w-full h-full object-contain" style={{ filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.5))' }} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <ChevronDown className="weapon-scroll-chev w-4 h-4 mx-auto mt-3" style={{ color: CRIMSON }} />
            </div>
          </div>
        ) : (
          /* ===== THROWABLES ===== */
          <div
            className="rounded-[22px] p-8"
            style={{
              background: 'linear-gradient(135deg, rgba(16,22,28,0.72), rgba(22,29,36,0.6))',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 24px 60px rgba(40,60,80,0.2)',
              backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
            }}
            data-testid="throwables-panel"
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {THROWABLES.map((t) => (
                <div key={t.id} data-testid={`throwable-${t.id}`} className="rounded-[14px] p-5 flex flex-col items-center text-center" style={{ background: 'rgba(18,24,34,0.82)', border: '1px solid rgba(120,138,152,0.28)' }}>
                  <div className="w-12 h-12 flex items-center justify-center mb-3" style={{ clipPath: 'none', borderRadius: '3px', background: 'rgba(255,134,24,0.14)', border: '1px solid rgba(255,122,0,0.45)' }}>
                    <Zap className="w-5 h-5" style={{ color: CRIMSON }} />
                  </div>
                  <p style={{ fontFamily: '"Russo One",sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', letterSpacing: '0.06em' }}>{t.name}</p>
                  <p style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t.type}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const Stat = ({ label, value }) => (
  <div className="pl-4" style={{ borderLeft: '2px solid #FF8618' }}>
    <p className="text-[10px] uppercase mb-1.5" style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, letterSpacing: '0.28em', color: STEEL }}>{label}</p>
    <p style={{ fontFamily: '"Russo One","Rajdhani",sans-serif', fontWeight: 700, fontSize: 'clamp(1.2rem,2vw,1.6rem)', color: NAVY }}>{value}</p>
  </div>
);

export default Weapons;
